import React from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import RichText from '@/components/RichText';
import { VideoMedia } from '@/components/VideoMedia';
import type { Media, MediaBlock as MediaBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

type Props = MediaBlockProps & {
  className?: string;
  fullscreen?: boolean;
  enableGutter?: boolean;
};

export const MediaBlock: React.FC<Props> = (props) => {
  const {
    backgroundColor,
    className,
    content,
    enableGutter = true,
    fullscreen,
    heading,
    headingAppearance,
    individualSpacing,
    label,
    medias,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;

  if (!medias) return null;

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      {(label || heading || content) && (
        <div className={cn('container', styles.content)}>
          {label && <div className={styles.label}>{label}</div>}
          {heading && (
            <h3 className={cn(styles.heading, headingAppearance)}>{heading}</h3>
          )}
          <RichText content={content || {}} enableGutter={false} />
        </div>
      )}

      <div
        className={cn(
          {
            container: fullscreen === false && enableGutter,
            'container container--full': fullscreen === true && enableGutter,
            [styles.single]: medias?.length === 1,
            [styles.multiple]: (medias?.length ?? 0) > 1,
          },
          className
        )}
      >
        {medias &&
          medias.map((media, index: number) => {
            const MediaComponent =
              media.mediaType === 'image' && media.image ? (
                <ImageMedia
                  aspectRatio={media.aspectRatio}
                  className={styles.image}
                  enableZoom={media.enableZoom as boolean}
                  image={media.image}
                  responsiveWidths={
                    media.aspectRatio === 'square'
                      ? {
                          mobile: 343,
                          desktop: 512,
                          largeDesktop: 2256,
                        }
                      : media.aspectRatio === 'portrait'
                        ? {
                            mobile: 343,
                            desktop: 512,
                            largeDesktop: 800,
                          }
                        : {
                            mobile: 343,
                            desktop: 1352,
                            largeDesktop: 2256,
                          }
                  }
                />
              ) : media.mediaType === 'video' && media.videoEmbedUrl ? (
                <VideoMedia
                  aspectRatio={media.aspectRatio}
                  videoPoster={media.videoPoster as Media}
                  videoEmbedUrl={media.videoEmbedUrl}
                  videoIsBackground={media.videoIsBackground as boolean}
                  videoLoops={media.videoLoops as boolean}
                />
              ) : null;

            if (!MediaComponent) return null;

            return (
              <div key={index} className={styles.item}>
                {MediaComponent}
                {media.caption && (
                  <RichText
                    content={media.caption}
                    className={styles.caption}
                    enableGutter={false}
                  />
                )}
              </div>
            );
          })}
      </div>
    </BlockSettings>
  );
};
